import React from 'react';
import {
  IconButton,
  Box,
  Typography
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import VoiceUploadService from '../services/voiceUploadService';

interface VoiceRecorderProps {
  recording: boolean;
  recordingDuration: number;
  audioPreview: string | null;
  onMicClick: () => void;
  onClearAudio: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  recording,
  recordingDuration,
  audioPreview,
  onMicClick,
  onClearAudio,
  disabled = false,
  size = 'small'
}) => {
  return (
    <>
      {/* Recording Status */}
      {recording && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: 'rgba(244, 67, 54, 0.1)', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: '#f44336',
            animation: 'pulse 1.5s infinite'
          }} />
          <Typography variant="body2" color="error">
            Recording... {VoiceUploadService.formatDuration(recordingDuration)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            (Click mic to stop)
          </Typography>
        </Box>
      )}

      {/* Audio Preview */}
      {audioPreview && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              🎤 Voice Preview ({VoiceUploadService.formatDuration(recordingDuration)})
            </Typography>
            <IconButton size="small" onClick={onClearAudio}>
              <CloseIcon />
            </IconButton>
          </Box>
          <audio 
            controls 
            src={audioPreview}
            style={{ 
              width: '100%',
              height: '32px'
            }}
          />
        </Box>
      )}
    </>
  );
};

export const VoiceMicButton: React.FC<VoiceRecorderProps> = ({
  recording,
  onMicClick,
  disabled = false,
  size = 'small'
}) => {
  return (
    <IconButton
      onClick={onMicClick}
      color={recording ? "secondary" : "primary"}
      size={size}
      disabled={disabled}
      sx={{
        ...(recording && {
          animation: 'pulse 1.5s infinite',
          backgroundColor: 'rgba(244, 67, 54, 0.1)'
        })
      }}
    >
      {recording ? <StopIcon /> : <MicIcon />}
    </IconButton>
  );
};

export default VoiceRecorder; 